import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Error';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogTitle,
  IconButton,
  Snackbar,
  Tooltip,
  Typography
} from '@mui/material';
import { capitalDesignTokens } from '@opengov/capital-mui-theme';
import { LocaleText } from '../../../locale';
import React, { useState } from 'react';
import { RenderIf } from '../../common/RenderIf';
import styles from './PreviewModal.module.scss';
import { PreviewModalDialogContent } from './PreviewModalDialogContent';

export interface IExportPdfPreviewModalProps {
  localeText: LocaleText;
  onClose(): void;
  options: React.ReactElement;
  handleExport(): void;
  previewLink: string;
  previewCount: number;
  showPdfLoadingSpinner: boolean;
  /** Shown in the preview pane instead of a preview, e.g. when the export data could not be loaded. */
  unavailableMessage?: string;
  /** Disables Export even when no preview is loading, e.g. after the preview failed to render. */
  isExportDisabled?: boolean;
  onSaveDefaultConfig?: () => Promise<void>;
  isSetAsDefaultDisabled?: boolean;
}

type SaveDefaultStatus = 'idle' | 'saving' | 'success' | 'error';

const toastIconFontSize = capitalDesignTokens.foundations.typography.fontSize.bodyLarge;

export const ExportPdfPreviewModal: React.FC<IExportPdfPreviewModalProps> = (props) => {
  const {
    onClose,
    localeText,
    handleExport,
    previewLink,
    previewCount,
    showPdfLoadingSpinner,
    unavailableMessage,
    isExportDisabled = false,
    onSaveDefaultConfig,
    isSetAsDefaultDisabled
  } = props;
  const { exportPdfModal } = localeText;

  const [saveStatus, setSaveStatus] = useState<SaveDefaultStatus>('idle');

  const handleSaveDefault = async () => {
    if (!onSaveDefaultConfig) return;
    setSaveStatus('saving');
    try {
      await onSaveDefaultConfig();
      setSaveStatus('success');
    } catch {
      setSaveStatus('error');
    }
  };

  const handleToastClose = () => {
    setSaveStatus('idle');
  };

  return (
    <Dialog
      onClose={onClose}
      open={true}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { overflow: 'hidden', display: 'flex', flexDirection: 'column' } }}
    >
      <DialogTitle>{exportPdfModal.title} </DialogTitle>
      <IconButton className="MuiDialog__close-button" aria-label="close" onClick={onClose}>
        <CloseIcon />
      </IconButton>

      <PreviewModalDialogContent
        localeText={localeText}
        options={props.options}
        previewLink={previewLink}
        previewCount={previewCount}
        showPdfLoadingSpinner={showPdfLoadingSpinner}
        unavailableMessage={unavailableMessage}
      />

      <DialogActions className={styles.dialogActions}>
        <Button onClick={onClose} data-test="button-cancel">
          {localeText.cancel}
        </Button>
        {onSaveDefaultConfig && (
          <Tooltip title={exportPdfModal.setAsDefaultTooltip} placement="top">
            <span>
              <Button
                variant="outlined"
                onClick={() => void handleSaveDefault()}
                disabled={saveStatus === 'saving' || Boolean(isSetAsDefaultDisabled)}
                startIcon={
                  <RenderIf condition={saveStatus === 'saving'}>
                    <CircularProgress size={16} color="inherit" />
                  </RenderIf>
                }
                data-test="button-set-as-default"
              >
                {exportPdfModal.setAsDefaultButton}
              </Button>
            </span>
          </Tooltip>
        )}
        <Button
          variant="contained"
          data-test="button-export"
          onClick={() => {
            handleExport();
            onClose();
          }}
          disabled={showPdfLoadingSpinner || isExportDisabled}
        >
          {exportPdfModal.downloadButton}
        </Button>
      </DialogActions>

      <Snackbar
        open={saveStatus === 'success'}
        autoHideDuration={5000}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        color="successSecondary"
        data-test="set-as-default-success-toast"
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
          <CheckCircleIcon sx={{ fontSize: toastIconFontSize }} />
          <Typography variant="h4" component="div" sx={{ flex: 1 }}>
            {exportPdfModal.setAsDefaultSuccessToast}
          </Typography>
          <IconButton aria-label="close success notification" size="small" onClick={handleToastClose}>
            <CloseIcon sx={{ fontSize: capitalDesignTokens.foundations.typography.fontSize.bodyDefault }} />
          </IconButton>
        </Box>
      </Snackbar>

      <Snackbar
        open={saveStatus === 'error'}
        autoHideDuration={5000}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        color="errorSecondary"
        data-test="set-as-default-error-toast"
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
          <ErrorIcon sx={{ fontSize: toastIconFontSize }} />
          <Typography variant="h4" component="div" sx={{ flex: 1 }}>
            {exportPdfModal.setAsDefaultErrorToast}
          </Typography>
          <IconButton aria-label="close error notification" size="small" onClick={handleToastClose}>
            <CloseIcon sx={{ fontSize: capitalDesignTokens.foundations.typography.fontSize.bodyDefault }} />
          </IconButton>
        </Box>
      </Snackbar>
    </Dialog>
  );
};
